import { useCallback, useEffect, useRef, useState } from 'react';
import kit from './kit.module.css';
import styles from './ConversationResetDemoVoice.module.css';

type Who = 'you' | 'agent' | 'cue';
type Step = 'offer' | 'resumed' | 'restarted' | 'done';

interface Line {
  id: number;
  who: Who;
  text: string;
}

type Script = Array<Omit<Line, 'id'>>;

const speakMs = (text: string) => Math.min(3200, 900 + text.length * 22);

const FEEDBACK: Record<Step, string> = {
  offer: 'The speaker restarted mid-recipe. Answer out loud by picking a reply.',
  resumed: 'You are back on the right step without repeating anything. Try asking what was done, or say next.',
  restarted: 'Starting over is always one phrase away - and so is getting back to where you were.',
  done: 'Recovered context, confirmed by you, and only what was actually saved. Press Reset to try another path.',
};

export function ConversationResetDemoVoice() {
  const [lines, setLines] = useState<Line[]>([]);
  const [speaking, setSpeaking] = useState(false);
  const [busy, setBusy] = useState(true);
  const [step, setStep] = useState<Step>('offer');
  const [askedDone, setAskedDone] = useState(false);
  const timers = useRef<number[]>([]);
  const nextId = useRef(0);

  const play = useCallback((script: Script, next: Step) => {
    setBusy(true);
    let t = 0;
    const at = (ms: number, fn: () => void) => timers.current.push(window.setTimeout(fn, ms));
    script.forEach((item, i) => {
      t += item.who === 'you' ? (i === 0 ? 0 : 400) : item.who === 'cue' ? 500 : 700;
      at(t, () => setLines((l) => [...l, { ...item, id: nextId.current++ }]));
      if (item.who === 'agent') {
        at(t, () => setSpeaking(true));
        t += speakMs(item.text);
        at(t, () => setSpeaking(false));
      }
    });
    at(t + 200, () => {
      setStep(next);
      setBusy(false);
    });
  }, []);

  useEffect(() => {
    play(
      [
        { who: 'cue', text: '(device restarts, soft startup chime)' },
        {
          who: 'agent',
          text: "I'm back. You were making banana bread, on step four - folding in the mashed bananas. Want to pick up there?",
        },
      ],
      'offer'
    );
    const pending = timers.current;
    return () => {
      pending.forEach(clearTimeout);
      pending.length = 0;
    };
  }, [play]);

  const resume = () =>
    play(
      [
        { who: 'you', text: 'Yes.' },
        { who: 'agent', text: "Okay. Step four: fold in the bananas until just combined. Say next when you're ready." },
      ],
      'resumed'
    );

  const askDone = () => {
    setAskedDone(true);
    play(
      [
        { who: 'you', text: 'What have I done so far?' },
        {
          who: 'agent',
          text: 'Steps one to three: oven at 350, dry ingredients mixed, butter and sugar creamed.',
        },
        { who: 'agent', text: "I didn't save whether a timer was running, so check that one yourself." },
      ],
      step === 'offer' ? 'offer' : 'resumed'
    );
  };

  const startOver = () =>
    play(
      [
        { who: 'you', text: 'Start over.' },
        { who: 'agent', text: 'Starting from the top. Step one: preheat the oven to 350 degrees.' },
      ],
      'restarted'
    );

  const backToFour = () =>
    play(
      [
        { who: 'you', text: 'Wait, go back to step four.' },
        { who: 'agent', text: 'Back to step four: fold in the mashed bananas until just combined.' },
      ],
      'resumed'
    );

  const next = () =>
    play(
      [
        { who: 'you', text: 'Next.' },
        { who: 'agent', text: 'Step five: pour the batter into the loaf pan and bake for 60 minutes.' },
      ],
      'done'
    );

  const reply = (label: string, onClick: () => void, variant: string) => (
    <button key={label} type="button" className={`${kit.btn} ${variant}`} onClick={onClick} disabled={busy}>
      Say: "{label}"
    </button>
  );

  let replies: JSX.Element[] = [];
  if (step === 'offer') {
    replies = [
      reply('Yes', resume, kit.btnApprove),
      ...(askedDone ? [] : [reply('What have I done so far?', askDone, kit.btnEdit)]),
      reply('Start over', startOver, kit.btnGhost),
    ];
  } else if (step === 'resumed') {
    replies = [
      reply('Next', next, kit.btnPrimary),
      ...(askedDone ? [] : [reply('What have I done so far?', askDone, kit.btnEdit)]),
    ];
  } else if (step === 'restarted') {
    replies = [reply('Wait, go back to step four', backToFour, kit.btnEdit)];
  }

  return (
    <div className={kit.root}>
      <div className={`${kit.orb} ${speaking ? kit.orbActive : ''}`} aria-hidden="true" />
      <p className={styles.status}>{speaking ? 'Speaking' : busy ? 'Listening' : 'Waiting for you'}</p>

      <div className={kit.transcript} aria-live="polite">
        {lines.map((line) =>
          line.who === 'cue' ? (
            <div key={line.id} className={kit.cue}>{line.text}</div>
          ) : (
            <div key={line.id} className={`${kit.voiceLine} ${line.who === 'agent' ? kit.voiceAgent : ''}`}>
              <span className={kit.voiceWho}>{line.who === 'agent' ? 'Agent' : 'You'}</span>
              <p>{line.text}</p>
            </div>
          )
        )}
      </div>

      {replies.length > 0 && <div className={`${kit.row} ${styles.replies}`}>{replies}</div>}

      <p className={kit.feedback} aria-live="polite">
        {busy && lines.length === 0 ? 'Restarting the kitchen speaker...' : FEEDBACK[step]}
      </p>
    </div>
  );
}
